import type { Address } from './facility';

/**
 * Match method used to detect organization membership
 */
export type MatchMethod =
  | 'operator_name' // 95% - Matched by operator/licensee name
  | 'prefix_separator' // 80-85% - "Name - Location" pattern
  | 'numbered_location' // 75% - "Name #1" pattern
  | 'token_match'; // 65% - First 2-3 words match

/**
 * Facility as returned in organization context
 */
export interface OrganizationFacility {
  readonly id: string;
  readonly name: string;
  readonly facility_type: string | null;
  readonly status: string | null;
  readonly operator_name: string | null;
  readonly address: Address;
  readonly province: string;
  readonly confidence: number; // 0-100
  readonly match_method: MatchMethod;
}

/**
 * Organization summary (without facilities)
 */
export interface Organization {
  readonly id: number;
  readonly name: string;
  readonly facility_count: number;
  readonly avg_confidence: number | null; // 0-100 or null
  readonly created_at: string; // ISO 8601 datetime
  readonly updated_at: string; // ISO 8601 datetime
}

/**
 * Organization with nested facilities
 */
export interface OrganizationWithFacilities extends Organization {
  readonly facilities: readonly OrganizationFacility[];
}

/**
 * Organization statistics
 */
export interface OrganizationStats {
  readonly total_organizations: number;
  readonly assigned_facilities: number;
  readonly total_facilities: number;
  readonly coverage_percentage: number; // 0-100
}

/**
 * Sortable fields for organizations
 */
export type OrganizationSortableField = 'facility_count' | 'avg_confidence' | 'name';

/**
 * Sort order
 */
export type OrganizationSortOrder = 'asc' | 'desc';

/**
 * Filters for organization queries
 */
export interface OrganizationFilters {
  readonly min_confidence?: number; // 0-100, default 70
  readonly min_facility_count?: number; // default 2
  readonly sort_by?: OrganizationSortableField;
  readonly sort_order?: OrganizationSortOrder;
  readonly limit?: number;
  readonly offset?: number;
}

/**
 * Get confidence level label
 */
export function getConfidenceLevel(
  confidence: number | null
): 'high' | 'medium' | 'low' {
  if (confidence === null) return 'low';
  if (confidence >= 90) return 'high';
  if (confidence >= 70) return 'medium';
  return 'low';
}

/**
 * Get human-readable match method description
 */
export function getMatchMethodDescription(method: MatchMethod): string {
  switch (method) {
    case 'operator_name':
      return 'Matched by operator name';
    case 'prefix_separator':
      return 'Name separator pattern (e.g., "Name - Location")';
    case 'numbered_location':
      return 'Numbered location pattern (e.g., "Name #1")';
    case 'token_match':
      return 'Name prefix match';
  }
}
